import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CalendarIcon, UserGroupIcon, StarIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import { TutorSessionCard } from '../../components/VideoSessionCard';
import StatCard from '../../components/StatCard';
import { CefrBadge } from '../../components/Badge';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';

const C = { teal:'#00B4D8', success:'#10B981', warning:'#F59E0B', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', error:'#EF4444', navy:'#0D1B2A' };

export default function TutorHome() {
  const [data,       setData]       = useState(null);
  const [hwSummary,  setHwSummary]  = useState([]);
  const [students,   setStudents]   = useState([]);
  const [reviewData, setReviewData] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const user = getUser();

  useEffect(() => {
    Promise.all([
      api.get('/api/tutor/dashboard').catch(() => null),
      api.get('/api/tutor/homework/class-summary').catch(() => null),
      api.get('/api/tutor/students').catch(() => null),
      api.get('/api/tutor/reviews').catch(() => null),
    ]).then(([dashRes, hwRes, studRes, revRes]) => {
      if (dashRes) setData(dashRes.data);
      if (hwRes)   setHwSummary(hwRes.data?.students || []);
      if (studRes) setStudents(studRes.data?.students || []);
      if (revRes)  setReviewData(revRes.data);
    }).finally(() => setLoading(false));
  }, []);

  const tutor = data?.tutor || {};
  const tenant = data?.tenant || {};
  const tenantColor = tenant?.primary_colour || C.teal;
  const tenantName = tenant?.school_name || '';

  const feeEnabled = data?.fee_info?.enabled !== false;
  const feePercent = data?.fee_info?.percent || 10;
  const earnings = data?.earnings || [];
  const totals = data?.totals || { gross: 0, fees: 0, net: 0 };
  const hourlyRate = tutor.hourly_rate || 0;
  const rating = tutor.rating || null;

  // Build monthly chart from earnings data
  const monthlyMap = {};
  earnings.forEach(e => {
    const d = new Date(e.created_at);
    const key = d.toLocaleString('en-GB', { month: 'short' });
    if (!monthlyMap[key]) monthlyMap[key] = 0;
    monthlyMap[key] += e.net_usd || 0;
  });
  const chartData = Object.entries(monthlyMap).slice(-6).map(([month, net]) => ({ month, net: parseFloat(net.toFixed(2)) }));

  return (
    <Layout role="tutor" tenantColor={tenantColor} tenantName={tenantName}>
      <TutorSessionCard />
      {/* Hero */}
      <div className="card" style={{ padding:28, marginBottom:24, background:`linear-gradient(135deg, ${tenantColor} 0%, ${tenantColor}cc 100%)`, color:'#fff' }}>
        <h2 style={{ margin:'0 0 8px', fontSize:22, fontWeight:700 }}>
          Welcome back, {tutor.first_name || user?.first_name || 'Tutor'}! 👋
        </h2>
        <div style={{ display:'flex', gap:24, flexWrap:'wrap', opacity:.9, fontSize:14 }}>
          {tenantName && <span>🏫 {tenantName}</span>}
          {tutor.tutor_id_number && <span>🪪 ID: {tutor.tutor_id_number}</span>}
          <span style={{ textTransform:'capitalize', fontWeight:600 }}>
            {tutor.is_approved ? '✅ Approved' : '⏳ Pending Approval'}
          </span>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap:20, marginBottom:24 }}>
        <StatCard title="Total Students" value={data?.total_students ?? students.length ?? '—'} icon={UserGroupIcon} color={C.teal} />
        <StatCard title="Total Lessons" value={earnings.length || '—'} icon={CalendarIcon} color="#8B5CF6" />
        <StatCard title="Total Earned" value={totals.net > 0 ? `$${totals.net.toFixed(0)}` : '—'} icon={CurrencyDollarIcon} color={C.success} />
        <div className="card" style={{ padding:20 }}>
          <div style={{ fontSize:12, fontWeight:600, color:C.grey, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:8 }}>Rating</div>
          {reviewData?.total > 0 ? (
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                <span style={{ fontSize:28, fontWeight:800, color:C.dark }}>{reviewData.average.toFixed(1)}</span>
                <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                  <div style={{ display:'flex', gap:1 }}>
                    {[1,2,3,4,5].map(s => (
                      <span key={s} style={{ fontSize:13, color: s <= Math.round(reviewData.average) ? '#F59E0B' : '#D1D5DB' }}>★</span>
                    ))}
                  </div>
                  <div style={{ fontSize:11, color:C.grey }}>{reviewData.total} review{reviewData.total !== 1 ? 's' : ''}</div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ fontSize:14, color:C.grey }}>No ratings yet</div>
          )}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:20, marginBottom:24 }}>
        {/* Earnings this month */}
        <div className="card" style={{ padding:24 }}>
          <h3 style={{ margin:'0 0 16px', fontSize:15, fontWeight:700 }}>Earnings Overview</h3>
          {totals.gross > 0 ? (
            <>
              {chartData.length > 0 && (
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={chartData}>
                    <XAxis dataKey="month" tick={{ fontSize:11 }} />
                    <YAxis tick={{ fontSize:11 }} tickFormatter={v=>`$${v}`} width={40} />
                    <Tooltip formatter={v=>[`$${v}`,'Net']} />
                    <Bar dataKey="net" fill={C.success} radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              <div style={{ background:'#F9FAFB', borderRadius:10, padding:16, fontFamily:'monospace', fontSize:13, border:`1px solid ${C.border}`, marginTop:12 }}>
                {feeEnabled ? (
                  <>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                      <span style={{ color:C.grey }}>Gross earned:</span><span style={{ fontWeight:700 }}>${totals.gross.toFixed(2)}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, color:C.warning }}>
                      <span>Platform fee ({feePercent}%):</span><span>-${totals.fees.toFixed(2)}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontWeight:800, color:C.success, borderTop:`1px solid ${C.border}`, paddingTop:8 }}>
                      <span>NET PAYOUT:</span><span>${totals.net.toFixed(2)} ✓</span>
                    </div>
                  </>
                ) : (
                  <div style={{ display:'flex', justifyContent:'space-between', fontWeight:800, color:C.success }}>
                    <span>Total Earned:</span><span>${totals.gross.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ textAlign:'center', padding:32, color:C.grey }}>
              <CurrencyDollarIcon style={{ width:40, height:40, color:C.border, margin:'0 auto 12px', display:'block' }} />
              <p style={{ margin:0, fontSize:14 }}>No earnings recorded yet. Earnings appear after completing lessons.</p>
            </div>
          )}
        </div>

        {/* Profile snapshot */}
        <div className="card" style={{ padding:24 }}>
          <h3 style={{ margin:'0 0 16px', fontSize:15, fontWeight:700 }}>My Profile</h3>
          {[
            ['Specializations', (tutor.specializations||[]).join(', ') || '—'],
            ['Native Language', tutor.native_language || '—'],
            ['Nationality', tutor.nationality || '—'],
            ['Hourly Rate', hourlyRate ? `$${hourlyRate}/hr` : '—'],
            ['Application', tutor.application_status ? tutor.application_status.charAt(0).toUpperCase() + tutor.application_status.slice(1) : '—'],
          ].map(([label, value]) => (
            <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px solid ${C.border}`, fontSize:14 }}>
              <span style={{ color:C.grey }}>{label}</span>
              <span style={{ fontWeight:600, color:C.dark, maxWidth:200, textAlign:'right' }}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Pre-class Homework Review */}
      {hwSummary.length > 0 && (
        <div className="card" style={{ padding:24, marginBottom:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>📋 Pre-class Homework Review</h3>
            <span style={{ fontSize:12, color:C.grey }}>Review before today's classes</span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:12 }}>
            {hwSummary.slice(0, 9).map((s, i) => {
              const sub = s.submission;
              const hw = s.homework;
              return (
                <div key={i} style={{ padding:14, background:'#F9FAFB', borderRadius:10, border:`1px solid ${C.border}` }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                    <div style={{ fontWeight:700, fontSize:13 }}>{s.student_name}</div>
                    <CefrBadge level={s.level} />
                  </div>
                  {!hw && <div style={{ fontSize:12, color:C.grey }}>No homework assigned</div>}
                  {hw && !sub && <div style={{ fontSize:12, color:'#D97706', fontWeight:600 }}>⏳ {hw.title} — not submitted</div>}
                  {hw && sub && (
                    <div>
                      <div style={{ fontSize:12, fontWeight:700, color:'#059669' }}>✅ {sub.percentage}% · {sub.score}/{hw.max_score}</div>
                      {sub.ai_feedback && <div style={{ fontSize:11, color:C.grey, marginTop:4, lineHeight:1.4 }}>{sub.ai_feedback.slice(0,80)}…</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Student Reviews */}
      {reviewData?.total > 0 && (
        <div className="card" style={{ padding:24, marginBottom:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>⭐ Student Reviews</h3>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:22, fontWeight:800, color:'#1F2937' }}>{reviewData.average.toFixed(1)}</span>
              <div>
                <div style={{ display:'flex', gap:1 }}>
                  {[1,2,3,4,5].map(s => (
                    <span key={s} style={{ fontSize:14, color: s <= Math.round(reviewData.average) ? '#F59E0B' : '#D1D5DB' }}>★</span>
                  ))}
                </div>
                <div style={{ fontSize:11, color:'#6B7280', textAlign:'right' }}>{reviewData.total} review{reviewData.total !== 1 ? 's' : ''}</div>
              </div>
            </div>
          </div>

          {/* Star distribution bars */}
          <div style={{ marginBottom:16, display:'flex', flexDirection:'column', gap:4 }}>
            {[5,4,3,2,1].map(star => {
              const count = reviewData.distribution?.[star] || 0;
              const pct   = reviewData.total > 0 ? (count / reviewData.total) * 100 : 0;
              return (
                <div key={star} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12 }}>
                  <span style={{ width:12, color:'#6B7280', textAlign:'right' }}>{star}</span>
                  <span style={{ color:'#F59E0B', fontSize:11 }}>★</span>
                  <div style={{ flex:1, height:8, background:'#F3F4F6', borderRadius:4, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${pct}%`, background:'#F59E0B', borderRadius:4, transition:'width 0.4s' }} />
                  </div>
                  <span style={{ width:20, color:'#6B7280', textAlign:'right' }}>{count}</span>
                </div>
              );
            })}
          </div>

          {/* Recent reviews list */}
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {(reviewData.reviews || []).slice(0, 5).map(r => (
              <div key={r.id} style={{ background:'#F9FAFB', borderRadius:10, padding:'12px 14px', border:'1px solid #E5E7EB' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:r.comment ? 6 : 0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:28, height:28, borderRadius:'50%', background:'#E5E7EB', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13 }}>👤</div>
                    <span style={{ fontWeight:600, fontSize:13, color:'#1F2937' }}>{r.student_name}</span>
                  </div>
                  <div style={{ display:'flex', gap:1 }}>
                    {[1,2,3,4,5].map(s => (
                      <span key={s} style={{ fontSize:13, color: s <= r.stars ? '#F59E0B' : '#D1D5DB' }}>★</span>
                    ))}
                  </div>
                </div>
                {r.comment && (
                  <div style={{ fontSize:13, color:'#4B5563', lineHeight:1.5, fontStyle:'italic', paddingLeft:36 }}>
                    "{r.comment}"
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Students */}
      <div className="card" style={{ padding:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>My Students</h3>
          {students.length > 0 && (
            <a href="/tutor/students" style={{ fontSize:13, color:C.teal, textDecoration:'none', fontWeight:600 }}>View all →</a>
          )}
        </div>
        {students.length === 0 ? (
          <div style={{ textAlign:'center', padding:32, color:C.grey }}>
            <UserGroupIcon style={{ width:40, height:40, color:C.border, margin:'0 auto 12px', display:'block' }} />
            <p style={{ margin:0, fontSize:14 }}>No students yet. Students appear here once you've taught lessons.</p>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:12 }}>
            {students.slice(0,6).map((s, i) => (
              <div key={i} style={{ padding:16, background:'#F9FAFB', borderRadius:10, border:`1px solid ${C.border}` }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <div style={{ fontWeight:600, fontSize:14 }}>{s.full_name || '—'}</div>
                  <CefrBadge level={s.cefr_level || 'A1'} />
                </div>
                <div style={{ fontSize:12, color:C.grey }}>Lessons: <strong>{s.lesson_count || 0}</strong></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
